import {
  Button,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  UseDisclosureReturn,
  useToast,
} from "@chakra-ui/react";
import React, { ReactNode } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { useReportError } from "../../helpers/useReportError";
import { AppModal } from "./AppModal";
import { AppModalContent } from "./AppModalContent";
import { AppModalFooter } from "./AppModalFooter";
import { modalSuccessToastProps } from "./modalToast";

export type FormModalProps<T extends FieldValues> = {
  disclosure: UseDisclosureReturn;
  form: UseFormReturn<T>;
  title: string;
  submitButtonText: ReactNode;
  children: ReactNode;

  /**
   * Return false when the submit was handled but the modal should stay open.
   * For example, when mapping a server validation error onto a field.
   *
   * Throw unexpected errors so ModalForm can report them.
   */
  onSubmit: (values: T) => Promise<void | false>;

  // provide a success message if you want a toast to appear on success
  successTitle?: string;

  // an error toast will always be displayed, but you can provide a custom title
  errorTitle?: string;

  size?: ModalProps["size"];

  colorScheme?: "primary" | "red";

  /**
   * When is the submit button enabled?
   * - valid: when the form is valid
   * - dirty: when the form is valid and at least one value has changed
   */
  submitCondition?: "valid" | "dirty";
};

/**
 * Low-config modal plus react-hook-form, so you don't need to wire everything
 * up yourself.
 *
 * When using the form to edit existing values, set the submitCondition to "dirty"
 *
 * If you need more configuration, then build your own modal directly as needed.
 */
export function FormModal<T extends FieldValues>({
  disclosure,
  form,
  title,
  submitButtonText,
  children,
  onSubmit,
  successTitle,
  errorTitle,
  size,
  colorScheme = "primary",
  submitCondition = "valid",
}: FormModalProps<T>): React.ReactElement {
  const {
    formState: { isSubmitting, isValid, isDirty },
  } = form;

  const reportError = useReportError();

  const toast = useToast();

  const submit = async (values: T) => {
    try {
      const result = await onSubmit(values);

      if (result === false) {
        return;
      }

      if (successTitle) {
        toast({
          title: successTitle,
          ...modalSuccessToastProps,
        });
      }

      disclosure.onClose();
    } catch (error) {
      reportError(error, {
        title: errorTitle ?? `${title} failed`,
      });
    }
  };

  const isSubmitDisabled =
    !isValid || isSubmitting || (submitCondition === "dirty" && !isDirty);

  return (
    <AppModal
      size={size}
      isOpen={disclosure.isOpen}
      onClose={disclosure.onClose}
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
    >
      <ModalOverlay />

      <AppModalContent
        as="form"
        noValidate
        onSubmit={form.handleSubmit(submit)}
      >
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />

        <ModalBody>{children}</ModalBody>

        <AppModalFooter>
          <Button
            type="button"
            variant="outline"
            colorScheme="gray"
            onClick={disclosure.onClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="solid"
            colorScheme={colorScheme}
            isDisabled={isSubmitDisabled}
            isLoading={isSubmitting}
          >
            {submitButtonText}
          </Button>
        </AppModalFooter>
      </AppModalContent>
    </AppModal>
  );
}
